import { ref, watch } from 'vue';
import useHaptics from '../helpers/use-haptics';

/**
 * Swipe-to-remove for a list row, modelled on the iOS list gesture.
 *
 * Module scope on purpose: only one row is open at a time across the whole list, which is what makes
 * the gesture feel deliberate instead of leaving a trail of half-open rows behind.
 */
const openRowId = ref<string | null>(null);

const ACTION_WIDTH = 76;
const OPEN_THRESHOLD = 24;
const AXIS_LOCK = 8; // px of travel before the gesture commits to horizontal or vertical

type Options = {
  getId: () => string;
  isEnabled: () => boolean;
};

/**
 * A swipe only uncovers the action; removing always takes a deliberate tap on the button. Downloads
 * works the same way, and a swipe that deletes on release is one careless gesture away from data loss.
 */
const useSwipeRow = ({ getId, isEnabled }: Options) => {
  const { light, medium } = useHaptics();

  const offset = ref(0);
  const isDragging = ref(false);
  // Whether the row has travelled far enough that letting go would leave the action uncovered.
  const hasPassedThreshold = ref(false);

  let startX = 0;
  let startY = 0;
  let startOffset = 0;
  let axis: 'x' | 'y' | null = null;

  const close = () => {
    offset.value = 0;
    if (openRowId.value === getId()) {
      openRowId.value = null;
    }
  };

  // Another row opening closes this one, and a recycled row must not inherit an open state.
  watch(openRowId, (id) => {
    if (id !== getId() && offset.value !== 0) {
      offset.value = 0;
    }
  });

  const onTouchStart = (event: TouchEvent) => {
    if (!isEnabled()) {
      return;
    }
    startX = event.touches[0].clientX;
    startY = event.touches[0].clientY;
    startOffset = offset.value;
    axis = null;
  };

  const onTouchMove = (event: TouchEvent) => {
    if (!isEnabled()) {
      return;
    }
    const deltaX = event.touches[0].clientX - startX;
    const deltaY = event.touches[0].clientY - startY;

    if (!axis) {
      if (Math.abs(deltaX) < AXIS_LOCK && Math.abs(deltaY) < AXIS_LOCK) {
        return;
      }
      // A vertical gesture belongs to the page, so it is handed back untouched.
      axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
    }
    if (axis === 'y') {
      return;
    }

    isDragging.value = true;
    // Nothing to gain past the action's own width, and the rubber band that far looks broken.
    offset.value = Math.min(0, Math.max(startOffset + deltaX, -ACTION_WIDTH * 1.5));

    // A tick as the row latches, so the point of no return is felt rather than guessed at. It
    // re-arms on the way back, which is what makes swiping in and out feel like a physical detent.
    const passed = offset.value <= -OPEN_THRESHOLD;
    if (passed !== hasPassedThreshold.value) {
      hasPassedThreshold.value = passed;
      light();
    }
  };

  const onTouchEnd = () => {
    if (!isEnabled() || axis !== 'x') {
      axis = null;
      return;
    }
    isDragging.value = false;
    axis = null;
    hasPassedThreshold.value = false;

    const shouldOpen = offset.value <= -OPEN_THRESHOLD;
    offset.value = shouldOpen ? -ACTION_WIDTH : 0;
    openRowId.value = shouldOpen ? getId() : null;
  };

  // The destructive tap itself, weightier than the ticks that led up to it.
  const confirmRemove = () => medium();

  return {
    offset,
    isDragging,
    close,
    confirmRemove,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    actionWidth: ACTION_WIDTH,
  };
};

export default useSwipeRow;
